import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
export class FormErrorService {
  getErrorMessage(
    control: AbstractControl | null | undefined,
    fieldName: string,
    customErrorMessages?: { [key: string]: string },
  ): string {
    if (!control || !(control.errors && (control.dirty || control.touched))) {
      return '';
    }

    const errors: ValidationErrors = control.errors;

    const keys = [
      'required',
      'invalidText',
      'minLengthExceeded',
      'maxLengthExceeded',
      'invalidFileType',
      'fileSizeExceeded',
      'maxAmountExceeded',
      'chainNotSelected',
      'addressFormat',
      'addressFormatUnknown',
      'invalidUrl',
      'invalidUrlProtocol',
      'backendError',
    ];

    for (const key of keys) {
      if (errors[key] && customErrorMessages?.[key]) {
        return customErrorMessages[key];
      }
    }

    if (errors['required']) {
      return `${fieldName} is required`;
    }

    if (errors['maxLengthExceeded']) {
      const requiredLength = errors['maxLengthExceeded'].maxLength;
      return `${fieldName} cannot exceed ${requiredLength} characters`;
    }

    if (errors['minLengthExceeded']) {
      const requiredLength = errors['minLengthExceeded'].minLength;
      return `${fieldName} must be at least ${requiredLength} characters`;
    }

    if (errors['maxAmountExceeded']) {
      return `${fieldName} cannot exceed transaction amount`;
    }

    if (errors['invalidText']) {
      return `${fieldName} can only contain letters, numbers, spaces, and hyphens`;
    }

    if (errors['invalidEmail']) {
      return 'Please enter a valid email address';
    }

    if (errors['email']) {
      return 'Please enter a valid email address';
    }

    if (errors['chainNotSelected']) {
      return 'Select a blockchain first';
    }

    if (errors['addressFormat']) {
      const { network, length, prefix, invalidChars } = errors['addressFormat'];
      if (invalidChars) {
        const chars = invalidChars.map((char: string) => `'${char}'`).join(', ');
        return `Invalid ${network} address. It contains characters that are not allowed${chars ? `: ${chars}` : ''}.`;
      }
      const start = prefix ? `start with '${prefix}' and ` : '';
      return `Invalid ${network} address. It should ${start}be ${length} characters long.`;
    }

    if (errors['addressFormatUnknown']) {
      return `${fieldName} is not a valid address on any supported network`;
    }

    if (errors['invalidFileType']) {
      const allowedTypes = errors['invalidFileType'].allowedTypes;
      return `Please attach a valid file. Supported formats: ${allowedTypes}`;
    }

    if (errors['fileSizeExceeded']) {
      const maxSize = errors['fileSizeExceeded'].maxSize;
      const actualSize = errors['fileSizeExceeded'].actualSize;
      return `File size (${actualSize}MB) exceeds the maximum limit of ${maxSize}MB`;
    }

    if (errors['invalidUrl']) {
      return 'Please enter a valid URL';
    }

    if (errors['invalidUrlProtocol']) {
      const allowed = errors['invalidUrlProtocol'].allowedProtocols;
      const allowedList = Array.isArray(allowed) ? allowed.join(', ') : allowed;
      return `URL must use one of the following protocols: ${allowedList}`;
    }

    if (errors['invalidAmount']) {
      return `${fieldName} is invalid`;
    }

    if (errors['exceedsBalance']) {
      const balance = errors['exceedsBalance'].balance;
      const currency = errors['exceedsBalance'].currency ?? '';
      return balance
        ? `${fieldName} exceeds available ${balance} ${currency}`.trim()
        : `${fieldName} exceeds available balance`;
    }

    if (errors['invalidPrecision']) {
      const precision = errors['invalidPrecision'].precision;
      return `${fieldName} cannot have more than ${precision} decimal places`;
    }

    if (errors['insufficientCurrencyBalance']) {
      return 'Insufficient balance for selected currency';
    }

    if (errors['backendError']) {
      return errors['backendError'];
    }

    return 'Invalid field';
  }
}
